import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { updateSafeAreaInsets, ZERO_SAFE_AREA } from '../state/safeArea';
import { useMobilePreviewContainer } from './MobilePreviewContext';
import {
  VIEWPORT_SYNC_EVENT,
  readEffectiveViewportMetrics,
} from '../utils/visualViewportMetrics';

type ViewportSize = {
  width: number;
  height: number;
};

const defaultViewport: ViewportSize = { width: 0, height: 0 };

const ViewportContext = createContext<ViewportSize>(defaultViewport);

const readViewportSize = (): ViewportSize => {
  if (typeof window === 'undefined') {
    return defaultViewport;
  }

  const metrics = readEffectiveViewportMetrics();

  return {
    width: window.innerWidth,
    height: metrics.visibleHeight,
  };
};

const readContainerSize = (container: HTMLElement): ViewportSize => ({
  width: container.clientWidth,
  height: container.clientHeight,
});

const readSafeAreaInsets = () => {
  if (typeof window === 'undefined') {
    return ZERO_SAFE_AREA;
  }

  const root = document.documentElement;
  const style = window.getComputedStyle(root);
  const read = (name: string) => {
    const value = Number.parseFloat(style.getPropertyValue(name));
    return Number.isFinite(value) ? value : 0;
  };

  return {
    top: read('--safe-top'),
    right: read('--safe-right'),
    bottom: read('--safe-bottom'),
    left: read('--safe-left'),
  };
};

type ProviderProps = {
  children: ReactNode;
};

export const ViewportProvider = ({ children }: ProviderProps) => {
  const previewContainer = useMobilePreviewContainer();
  const [viewport, setViewport] = useState<ViewportSize>(() =>
    previewContainer ? readContainerSize(previewContainer) : readViewportSize(),
  );

  useEffect(() => {
    if (previewContainer) {
      const readContainerSafeArea = () => {
        const style = window.getComputedStyle(previewContainer);
        const read = (name: string) => {
          const v = Number.parseFloat(style.getPropertyValue(name));
          return Number.isFinite(v) ? v : 0;
        };
        return {
          top: read('--safe-top'),
          right: read('--safe-right'),
          bottom: read('--safe-bottom'),
          left: read('--safe-left'),
        };
      };

      const handleResize = () => {
        const size = readContainerSize(previewContainer);
        setViewport(size);
        updateSafeAreaInsets(readContainerSafeArea());
        previewContainer.style.setProperty('--vvh', `${size.height * 0.01}px`);
      };

      handleResize();

      const observer = new ResizeObserver(handleResize);
      observer.observe(previewContainer);
      return () => observer.disconnect();
    }

    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      setViewport(readViewportSize());
      updateSafeAreaInsets(readSafeAreaInsets());
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener(VIEWPORT_SYNC_EVENT, handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('scroll', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener(VIEWPORT_SYNC_EVENT, handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('scroll', handleResize);
    };
  }, [previewContainer]);

  const value = useMemo(() => viewport, [viewport]);

  return <ViewportContext.Provider value={value}>{children}</ViewportContext.Provider>;
};

export const useViewportSize = () => {
  const context = useContext(ViewportContext);
  return context;
};
